import {
  VESSEL_CARD_EXPECTED_CURVE_AUXILIARY,
  VESSEL_CARD_EXPECTED_CURVE_EXPONENT,
  VESSEL_CARD_EXPECTED_CURVE_INTERVAL,
  VESSEL_CARD_EXPECTED_CURVE_MAX_POINTS,
  VESSEL_CARD_EXPECTED_CURVE_SPEED_PRECISION,
} from "../constants/vessel-card-chart.constants";
import type { VesselCardChartPoint, VesselCardChartRow, VesselCardExpectedCurve } from "../models/vessel-card.models";

const availableMetric = (value: number | null): value is number =>
  value !== null && Number.isFinite(value) && value >= 0;

const getExpectedPoint = (row: VesselCardChartRow): VesselCardChartPoint | null =>
  availableMetric(row.expectedSpeed) && row.expectedSpeed > 0 && availableMetric(row.expectedConsumption)
    ? { speed: row.expectedSpeed, consumption: row.expectedConsumption, mode: row.mode }
    : null;

// Workbook endpoints use the comparison consumption when supplied, otherwise the DVP consumption.
// The inverse speed formula deliberately has no auxiliary deduction and rounds speed to one decimal.
const getCurveEndpoint = (row: VesselCardChartRow): VesselCardChartPoint | null => {
  const expected = getExpectedPoint(row);
  const consumption = row.comparisonConsumption ?? row.expectedConsumption;
  if (!expected || expected.consumption <= 0 || !availableMetric(consumption) || consumption <= 0) return null;
  const speed =
    Math.round(
      expected.speed *
        (consumption / expected.consumption) ** (1 / VESSEL_CARD_EXPECTED_CURVE_EXPONENT) *
        VESSEL_CARD_EXPECTED_CURVE_SPEED_PRECISION,
    ) / VESSEL_CARD_EXPECTED_CURVE_SPEED_PRECISION;
  return Number.isFinite(speed) && speed > 0 ? { speed, consumption, mode: row.mode } : null;
};

// Call with one loading condition. Match ECO/FULL by name, never by array order or comparison speed.
// C14/G14's recurrence is equivalent to calculating each interior point from the first endpoint.
// The last point is pinned to C35/G39, even when it differs from the continuous power curve.
export const getVesselCardExpectedCurve = (rows: VesselCardChartRow[]): VesselCardExpectedCurve => {
  const sourcePoints = rows
    .flatMap((row) => {
      const point = getExpectedPoint(row);
      return point ? [point] : [];
    })
    .sort((left, right) => left.speed - right.speed);
  const modeName = (row: VesselCardChartRow) => row.mode.toLowerCase().replaceAll("_", " ").replaceAll("-", " ").trim();
  const eco = rows.filter((row) => modeName(row).endsWith(" eco"));
  const full = rows.filter((row) => modeName(row).endsWith(" full"));
  if (eco.length !== 1 || full.length !== 1) return { points: [], endpoints: sourcePoints, available: false };
  const first = getCurveEndpoint(eco[0]);
  const last = getCurveEndpoint(full[0]);
  if (!first || !last) return { points: [], endpoints: sourcePoints, available: false };
  const endpoints = [first, last];
  const steps = Math.round((last.speed - first.speed) / VESSEL_CARD_EXPECTED_CURVE_INTERVAL);
  if (
    steps <= 0 ||
    steps + 1 > VESSEL_CARD_EXPECTED_CURVE_MAX_POINTS ||
    first.consumption <= VESSEL_CARD_EXPECTED_CURVE_AUXILIARY ||
    last.consumption <= VESSEL_CARD_EXPECTED_CURVE_AUXILIARY
  )
    return { points: [], endpoints, available: false };

  const interior = Array.from({ length: steps - 1 }, (_, index) => {
    const speed =
      Math.round(
        (first.speed + (index + 1) * VESSEL_CARD_EXPECTED_CURVE_INTERVAL) * VESSEL_CARD_EXPECTED_CURVE_SPEED_PRECISION,
      ) / VESSEL_CARD_EXPECTED_CURVE_SPEED_PRECISION;
    return {
      speed,
      consumption:
        (first.consumption - VESSEL_CARD_EXPECTED_CURVE_AUXILIARY) *
          (speed / first.speed) ** VESSEL_CARD_EXPECTED_CURVE_EXPONENT +
        VESSEL_CARD_EXPECTED_CURVE_AUXILIARY,
      mode: first.mode,
    };
  });
  if (interior.some((point) => !Number.isFinite(point.consumption))) {
    return { points: [], endpoints, available: false };
  }
  return { points: [first, ...interior, last], endpoints, available: true };
};
